/**
 * 基本設計の行を読みやすく出すための小さな変換（色分け・テストの 3 段・ノードの分割・件数・保存エラーの分割と置き場所）。
 *
 * 色は「どの行が目立つべきか」という画面の判断なので画面が持つ。値の一覧と表示名は API の
 * 選択肢が持つので、ここは値をキーにした色だけを持ち、知らない値は既定の色に落とす
 * （値が増えても表示が壊れず、色が付かないだけで済む）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 基本設計の節の行を、色分け・テストの 3 段表示・件数の集計・保存エラーの行と欄への振り分けで読みやすくする
 * @graph-connects none
 */

import type { BdChoice, BdIssue } from '../../../lib/api/basic-design.js';

/** バッジの色。`@cortex/web-design-system` の Badge の variant と同じ並び。 @graph-connects none */
export type BdTone = 'default' | 'success' | 'warning' | 'error' | 'info';

/**
 * 色を付ける列と、その値ごとの色。
 *
 * 劣化と未対応を赤にするのは、読み手がまず拾うべき行だから。影響なし・確認は情報量が少ないので
 * 既定の灰色にする。
 * @graph-connects none
 */
const TONES: Readonly<Record<string, Readonly<Record<string, BdTone>>>> = {
  direction: {
    new: 'info',
    degradation: 'error',
    improvement: 'success',
    dependency: 'warning',
    safe: 'default',
  },
  testType: { new: 'info', regression: 'warning', smoke: 'default' },
  state: { open: 'error', mitigated: 'info', accepted: 'warning', closed: 'success' },
  impact: {
    new: 'success',
    modified: 'warning',
    removed: 'error',
    unchanged: 'default',
    reused: 'info',
  },
  lockRisk: { yes: 'error', unknown: 'warning', no: 'default' },
  operation: { delete: 'error', backfill: 'warning', update: 'info', add: 'success' },
  severity: { high: 'error', medium: 'warning', low: 'default' },
  status: { open: 'warning', confirmed: 'success', superseded: 'default' },
  applicable: { yes: 'info', no: 'default' },
  foundational: { yes: 'info', no: 'default' },
};

/** 列の値の色。色の決まっていない列・値は既定の色。 @graph-connects none */
export function toneOf(column: string, value: string): BdTone {
  return TONES[column]?.[value] ?? 'default';
}

/** 絞り込みのボタン（TabButton）の件数の色。Badge の error は TabButton では danger と呼ぶ。 @graph-connects none */
export type BdTabTone = 'default' | 'danger' | 'warning' | 'info' | 'success';

/** 列の値の、絞り込みボタンでの色。 @graph-connects none */
export function tabToneOf(column: string, value: string): BdTabTone {
  const tone = toneOf(column, value);
  return tone === 'error' ? 'danger' : tone;
}

/** テストの 3 段。 @graph-connects none */
export interface BdTestSteps {
  given: string;
  when: string;
  then: string;
}

/** Given / When / Then の並び。区切りの矢印とコロンは無くても読む。 @graph-connects none */
const TEST_STEPS =
  /given\s*[:：]?\s*([\s\S]*?)\s*(?:→|->)?\s*when\s*[:：]?\s*([\s\S]*?)\s*(?:→|->)?\s*then\s*[:：]?\s*([\s\S]*)/i;

/**
 * テストの文を Given / When / Then の 3 段に分ける。この順で 3 つ揃っていなければ null
 * （呼び出し側は書かれたままの文を出す）。
 * @graph-connects none
 */
export function splitTestSteps(test: string): BdTestSteps | null {
  const match = TEST_STEPS.exec(test);
  if (match === null) return null;
  const [, given = '', when = '', then = ''] = match;
  return { given: given.trim(), when: when.trim(), then: then.trim() };
}

/** フローのノードの欄を 1 つずつに分ける（API の突き合わせと同じ区切り）。 @graph-connects none */
export function splitFlowNodes(text: string): string[] {
  return text
    .split(/[,、\s]+/)
    .map((token) => token.trim())
    .filter((token) => token !== '');
}

/** 選択肢 1 つあたりの件数。 @graph-connects none */
export interface BdTally {
  value: string;
  label: string;
  count: number;
}

/**
 * 値ごとの件数を、選択肢の並びで返す。0 件の選択肢も残す（「劣化は 0 件」も読み手には情報）。
 * 選択肢に無い値（空欄や古い値）は後ろに足す。
 * @graph-connects none
 */
export function tally(
  values: readonly string[],
  choices: readonly BdChoice[] | undefined,
  unselectedLabel: string,
): BdTally[] {
  const counts = new Map<string, number>();
  for (const value of values) counts.set(value, (counts.get(value) ?? 0) + 1);

  const known = (choices ?? []).map((c) => ({
    value: c.value,
    label: c.label,
    count: counts.get(c.value) ?? 0,
  }));
  const knownValues = new Set(known.map((k) => k.value));
  const unknown = [...counts.entries()]
    .filter(([value]) => !knownValues.has(value))
    .map(([value, count]) => ({ value, label: value === '' ? unselectedLabel : value, count }));
  return [...known, ...unknown];
}

/**
 * 400 の `details.issues`（API が保存を止めた理由の並び）を取り出す。形が違うものは捨てる。
 *
 * 文（message）は理由を ` / ` で連ねたものだが、選択肢の案内の中にも ` / ` があるので、
 * 文を区切って理由に戻すことはできない。行と列は文から読まず、指摘の `row` / `column` を使う。
 * @graph-connects none
 */
export function saveIssues(details: Readonly<Record<string, unknown>> | undefined): BdIssue[] {
  const issues = details?.issues;
  if (!Array.isArray(issues)) return [];
  return issues.filter(
    (issue): issue is BdIssue =>
      typeof issue === 'object' &&
      issue !== null &&
      typeof (issue as Partial<BdIssue>).message === 'string' &&
      typeof (issue as Partial<BdIssue>).text === 'string',
  );
}

/**
 * 送った行がフォームの何番目だったか（0 から）。API の `row` は送った行の数え方で、
 * フォームは全部空の行を送らないので、この並びで引き直す。送ったあとで消した行は null。
 * @graph-connects none
 */
export type BdSentRows = readonly (number | null)[];

/** 行を複製したあとの位置。複製した行より後ろが 1 つずれる。 @graph-connects none */
export function duplicateSentRow(sent: BdSentRows, index: number): (number | null)[] {
  return sent.map((i) => (i !== null && i > index ? i + 1 : i));
}

/** 行を消したあとの位置。消した行は null にして、その行の理由は出さない。 @graph-connects none */
export function removeSentRow(sent: BdSentRows, index: number): (number | null)[] {
  return sent.map((i) => {
    if (i === index) return null;
    return i !== null && i > index ? i - 1 : i;
  });
}

/** 欄の理由を引くキー。 @graph-connects none */
export function fieldErrorKey(row: number, column: string): string {
  return `${String(row)}:${column}`;
}

/** 保存エラーの置き場所。 @graph-connects none */
export interface BdPlacedErrors {
  /** フォームの行（0 から）ごとの、欄に付かない理由。その行のカードの上に出す */
  byRow: Map<number, string[]>;
  /** `fieldErrorKey(行, 列)` ごとの理由。その欄の下に出す */
  byField: Map<string, string[]>;
  /** 理由のあるフォームの行（0 から、昇順） */
  rows: number[];
  /** 行に付けられない理由。フォームの下にまとめて出す */
  general: string[];
}

/** Map の値の並びに足す。 @graph-connects none */
function append<K>(map: Map<K, string[]>, key: K, value: string): void {
  map.set(key, [...(map.get(key) ?? []), value]);
}

/**
 * 保存を止めた指摘を、欄に出すもの・行のカードに出すもの・まとめて出すものに分ける。
 *
 * 行に付けたものはカードにだけ出し、文は行・列の前置きの無い `message` にする（カードの
 * 「N 行目」と API の「N 行目」は空の行を送らないぶん食い違うため）。消した行の理由は捨てる。
 * 注意（warning）は保存を止めないので、ここには載せない。
 * @graph-connects fieldErrorKey [calls] 欄のキー
 */
export function placeSaveErrors(issues: readonly BdIssue[], sent: BdSentRows): BdPlacedErrors {
  const byRow = new Map<number, string[]>();
  const byField = new Map<string, string[]>();
  const rows = new Set<number>();
  const general: string[] = [];
  for (const issue of issues) {
    if (issue.severity !== 'error') continue;
    const formIndex = issue.row === undefined ? undefined : sent[issue.row];
    if (formIndex === undefined) {
      general.push(issue.text);
      continue;
    }
    if (formIndex === null) continue;
    rows.add(formIndex);
    if (issue.column === undefined) append(byRow, formIndex, issue.message);
    else append(byField, fieldErrorKey(formIndex, issue.column), issue.message);
  }
  return { byRow, byField, rows: [...rows].sort((a, b) => a - b), general };
}
